
import React, { useState, useEffect } from 'react';
import { LeadFilters, LeadType, LeadQuality, LeadStatus, LeadOutcome } from '../types/LeadTypes';
import { FilterButtons } from './FilterButtons';
import { DateRangeFilter } from './DateRangeFilter';
import { getStatusOptions, getOutcomeOptions, getTypeOptions } from './filterOptions';
import { useIsMobile } from '@/hooks/use-mobile';
import { QualityFilter } from './QualityFilter';
import { RevenueFilter } from './RevenueFilter';
import { TypeStatusOutcomeFilters } from './TypeStatusOutcomeFilters';
import { FilterSection } from './FilterSection';

interface LeadListFilterProps {
  filters: LeadFilters;
  onFiltersChange: (filters: LeadFilters) => void;
  onClose: () => void;
}

const LeadListFilter: React.FC<LeadListFilterProps> = ({ 
  filters, 
  onFiltersChange,
  onClose 
}) => {
  const [localFilters, setLocalFilters] = useState<LeadFilters>({...filters});
  const isMobile = useIsMobile();
  
  useEffect(() => {
    onFiltersChange(localFilters);
  }, [localFilters, onFiltersChange]);
  
  const handleResetFilters = () => {
    const resetFilters: LeadFilters = {
      statuses: [],
      outcomes: [],
      minValue: '',
      maxValue: '',
      types: [],
      leadQuality: ['qualified', 'disqualified'],
      dateRange: {
        from: undefined,
        to: undefined,
      }
    };
    setLocalFilters(resetFilters);
  };
  
  const handleStatusChange = (selectedValues: string[]) => {
    setLocalFilters(prev => ({
      ...prev,
      statuses: selectedValues as LeadStatus[]
    }));
  };
  
  const handleOutcomeChange = (selectedValues: string[]) => {
    setLocalFilters(prev => ({
      ...prev,
      outcomes: selectedValues as LeadOutcome[]
    }));
  };
  
  const handleTypeChange = (selectedValues: string[]) => {
    setLocalFilters(prev => ({
      ...prev,
      types: selectedValues as LeadType[]
    }));
  };
  
  const handleDateRangeChange = (dateRange: { from: Date | undefined; to: Date | undefined }) => {
    setLocalFilters(prev => ({
      ...prev,
      dateRange
    }));
  };

  const handleLeadQualityChange = (quality: LeadQuality) => {
    setLocalFilters(prev => {
      const currentQuality = [...prev.leadQuality];
      const index = currentQuality.indexOf(quality);
      
      if (index >= 0) {
        currentQuality.splice(index, 1);
      } else {
        currentQuality.push(quality);
      }
      
      return {
        ...prev,
        leadQuality: currentQuality
      };
    });
  };

  const handleMinValueChange = (value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      minValue: value
    }));
  };

  const handleMaxValueChange = (value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      maxValue: value
    }));
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <QualityFilter 
          leadQuality={localFilters.leadQuality}
          onLeadQualityChange={handleLeadQualityChange}
        />
        
        <TypeStatusOutcomeFilters 
          statusOptions={getStatusOptions()}
          typeOptions={getTypeOptions()}
          outcomeOptions={getOutcomeOptions()}
          selectedStatuses={localFilters.statuses}
          selectedTypes={localFilters.types}
          selectedOutcomes={localFilters.outcomes}
          onStatusChange={handleStatusChange}
          onTypeChange={handleTypeChange}
          onOutcomeChange={handleOutcomeChange}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <RevenueFilter 
          minValue={localFilters.minValue}
          maxValue={localFilters.maxValue}
          onMinValueChange={handleMinValueChange}
          onMaxValueChange={handleMaxValueChange}
          isMobile={isMobile}
        />
        
        <FilterSection id="date-range" label="Date">
          <DateRangeFilter 
            dateRange={localFilters.dateRange}
            onDateChange={handleDateRangeChange}
            isMobile={isMobile}
          />
        </FilterSection>
        
        <div></div>
        
        <div className="flex items-end justify-end">
          <FilterButtons 
            onReset={handleResetFilters}
            showApplyButton={false}
          />
        </div>
      </div>
    </div>
  );
};

export default LeadListFilter;
